import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useLocationStore } from '@/store/location';
import { toast } from 'sonner';

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  message: string | null;
  data: Record<string, unknown> | null;
  location_id: string | null;
  user_id: string | null;
  read_at: string | null;
  created_at: string;
  // Joined data
  location?: { name: string } | null;
}

// Re-export as Notification for backwards compatibility
export type { AppNotification as Notification };

export type NotificationType =
  | 'device_approval_request'
  | 'assistance_request'
  | 'low_stock'
  | 'order_issue'
  | 'system'
  | 'terminal_offline_alert';

/**
 * Hook for managing notifications
 * - Superadmin: sees all notifications
 * - Owner/Admin: sees notifications for their organization
 * - Manager: sees notifications for their assigned locations
 */
export function useNotifications() {
  const { user, profile } = useAuth();
  const { currentLocation } = useLocationStore();
  const queryClient = useQueryClient();

  const isSuperadmin = profile?.role === 'superadmin';
  const isAdmin = profile?.role && ['primary_admin', 'owner', 'admin'].includes(profile.role);
  const isManager = profile?.role === 'manager';
  const canViewNotifications = isSuperadmin || isAdmin || isManager;

  // Fetch notifications
  const {
    data: notifications,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['notifications', user?.id, profile?.organization_id, currentLocation?.id],
    queryFn: async () => {
      if (!user?.id || !profile?.organization_id) return [];

      // Get locations user has access to
      let locationIds: string[] = [];

      if (isSuperadmin || isAdmin) {
        // Superadmin/Admin sees all locations in their org
        const { data: orgLocations } = await supabase
          .from('locations')
          .select('id')
          .eq('organization_id', profile.organization_id);
        locationIds = orgLocations?.map(l => l.id) || [];
      } else if (isManager) {
        // Manager sees only their assigned locations
        const { data: accessData } = await supabase
          .from('user_location_access')
          .select('location_id')
          .eq('user_id', user.id);
        locationIds = accessData?.map(a => a.location_id) || [];
        if (locationIds.length === 0) {
          return []; // No access to any location
        }
      }

      if (locationIds.length === 0) return [];

      // Fetch notifications that are either:
      // 1. Targeted to this specific user (user_id matches)
      // 2. Broadcast notifications (user_id is null) for locations they can see
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          location:locations!notifications_location_id_fkey(name)
        `)
        .in('location_id', locationIds)
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      return (data || []).map(n => ({
        ...n,
        location: Array.isArray(n.location) ? n.location[0] : n.location,
      })) as AppNotification[];
    },
    enabled: !!user?.id && !!profile?.organization_id && canViewNotifications,
    refetchInterval: 30000, // Poll every 30 seconds
  });

  // Get unread count
  const unreadCount = notifications?.filter(n => !n.read_at).length ?? 0;

  // Mark notification as read
  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read
  const markAllAsRead = useMutation({
    mutationFn: async () => {
      if (!notifications?.length) return;

      const unreadIds = notifications.filter(n => !n.read_at).map(n => n.id);
      if (unreadIds.length === 0) return;

      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .in('id', unreadIds);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Delete notification
  const deleteNotification = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Real-time subscription for new notifications
  useEffect(() => {
    if (!user?.id || !profile?.organization_id || !canViewNotifications) return;

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          const newNotification = payload.new as AppNotification;

          // Check if this notification is for a location we can see
          // For now, just refetch to apply proper filtering
          queryClient.invalidateQueries({ queryKey: ['notifications'] });

          // Show toast for new notification
          toast(newNotification.title, {
            description: newNotification.message || undefined,
          });

          // Show browser notification if permission granted
          if ('Notification' in window && window.Notification.permission === 'granted') {
            try {
              new window.Notification(newNotification.title, {
                body: newNotification.message || undefined,
                icon: '/blaze-icon.svg',
                tag: newNotification.id, // Prevent duplicate notifications
              });
            } catch {
              // Browser notification failed (e.g., not supported in this context)
            }
          }

          // Play notification sound
          try {
            const audio = new Audio('/sounds/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {
              // Ignore audio play errors (user hasn't interacted yet)
            });
          } catch {
            // Audio not available
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, profile?.organization_id, canViewNotifications, queryClient, toast]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && window.Notification.permission === 'default') {
      // We'll request permission when user first interacts, not automatically
      // This is handled in the NotificationBell component or Settings
    }
  }, []);

  return {
    notifications: notifications ?? [],
    unreadCount,
    isLoading,
    canViewNotifications,
    refetch,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
}

/**
 * Create a notification (used by system/hooks when events occur)
 */
export async function createNotification({
  type,
  title,
  message,
  locationId,
  userId,
  data,
}: {
  type: NotificationType;
  title: string;
  message?: string;
  locationId: string;
  userId?: string;
  data?: Record<string, unknown>;
}) {
  const { error } = await supabase.from('notifications').insert({
    type,
    title,
    message: message || null,
    location_id: locationId,
    user_id: userId || null,
    data: data || null,
  });

  if (error) {
    console.error('Failed to create notification:', error);
    throw error;
  }
}
